import React, { type ReactNode } from "react";
import Card, {type Props as CardProps} from "@react/widget/Card";
import clsx from "clsx";
import Icon from "@react/widget/Icon";
import { H3 } from '@react/widget/Headline';
import { Skeleton } from "@nextui-org/skeleton";

const fontsizes = {
	"md": "[&_h3]:text-sm [&_h3]:md:text-base [&_p]:text-sm",
	"lg": "[&_h3]:text-base [&_h3]:md:text-xl [&_p]:text-sm [&_p]:md:text-base"
};

interface Props extends CardProps {
    isLoaded?: boolean;
    className?: string;
    href?: string;
    link?: string;
    type?: ReactNode;
    ebookCategory?: ReactNode;
    blogFeaturedImage?: ReactNode;
    logo?: ReactNode;
    topics?: ReactNode;
    readtimes?: ReactNode;
    title?: ReactNode;
    description?: ReactNode;
    keywords?: ReactNode;
    date?: ReactNode;
    summary?: ReactNode;
    author?: ReactNode;
    cta?: ReactNode;
    tags?: ReactNode;
    video?: boolean;
    logoSpace?: string;
    fontsize?: keyof typeof fontsizes;
    target?: '_self' | '_blank';
}

const BlogsCard:React.FC<Props> = ({
    isLoaded=true,
    className="",
    href,
    link,
    fontsize="lg",
    type,
    ebookCategory,
    blogFeaturedImage,
    logo,
    topics,
    readtimes,
    title,
    description,
    keywords,
    date,
    summary,
    author,
    cta,
    tags,
    video=false,
    target="_self",
    logoSpace="p-4",
    ...props
}) => {
    return (
    <Card bgColor='transparent' space='p-0' className={clsx("relative [&>img]:aspect-[16/9] [&>a]:bg-gray-50 items-stretch max-w-md mx-auto hover:cursor-pointer transition duration-500 hover:shadow-lg ![&:hover_h3]:decoration-gray", fontsizes[fontsize], className)} {...props}>
        <div>
            <Skeleton isLoaded={isLoaded} className="relative pt-[56%] overflow-hidden">
                { !!type && <span className="inline-block rounded px-4 mr-2.5 tag text-xs py-1.5 bg-white absolute top-5 left-5 z-10">{type}</span> }
                { blogFeaturedImage }
            </Skeleton>
        </div>
        <div className="flex flex-col gap-2 h-full bg-white p-5 border-r border-b border-l rounded-b-lg relative justify-between">
            <div className="space-y-3">
                <div>
                    { (!!topics || !!readtimes) && (
                    <div className="flex flex-wrap gap-1">
                        { isLoaded ? <>{ topics }{ readtimes }</> : <>
                            <Skeleton className="w-2/12 h-6 rounded-lg"></Skeleton>
                            <Skeleton className="w-4/12 h-6 rounded-lg"></Skeleton>
                            <Skeleton className="w-3/12 h-6 rounded-lg"></Skeleton>
                        </> }
                    </div>
                    ) }
                </div>
                <div>
                    { isLoaded ? <>
                        { !!title && (
                        <H3 className={clsx("font-semibold [&_a:hover]:text-primary [&_a]:transition [&_a]duration-500", !!logo && "mt-8")}>
                            {title}
                            { (target === "_blank") && <Icon className="w-4 h-4 text-red-500 inline-block" name="goto"/> }
                        </H3>
                        )}
                        { !!description && <div className="mt-3">{description}</div> }
                        { keywords }
                        {summary}
                        { !!date && <div className="text-sm mt-2">{date}</div> }
                    </> : <>
                        <Skeleton className="w-full h-8 rounded-lg"></Skeleton>
                        <Skeleton className="w-9/12 h-8 mt-1 rounded-lg"></Skeleton>
                        {/* <Skeleton className="w-full h-6 mt-2"></Skeleton>
                        <Skeleton className="w-10/12 h-6 mt-1"></Skeleton> */}
                    </> }
                </div>
                { !!tags && <div className="flex flex-wrap gap-1 !mt-4">
                    { isLoaded ? tags : <>
                        <Skeleton className="w-4/12 h-6 rounded-lg"></Skeleton>
                        <Skeleton className="w-3/12 h-6 rounded-lg"></Skeleton>
                    </> }
                </div>}
            </div>
            <div>
              { isLoaded ? <>
                  {cta}
              </> : <>
                  <Skeleton className="w-6/12 h-6 rounded-lg"></Skeleton>
              </> }
            </div>
        </div>
    </Card>
    );
};

export default BlogsCard;